import { useState, useRef, useEffect } from 'react';
import { AddClipButton, ClipAttachment } from './ClipRecordingIndicator';

export default function MeetingNotes({
  notes,
  onAddNote,
  isOpen,
  onClose,
  userName,
  isPremium,
  meetingStartTime,
  // Audio clip props
  audioClipState,
  onStartClipRecording,
  onPlayClip,
  pendingClip,
  onClearPendingClip
}) {
  const [noteText, setNoteText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const notesEndRef = useRef(null);

  const scrollToBottom = () => {
    notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [notes]);

  const getMeetingDuration = () => {
    if (!meetingStartTime) return 0;
    return Math.floor((Date.now() - meetingStartTime) / 1000 / 60); // minutes
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleAddNote = (e) => {
    e.preventDefault();
    if (noteText.trim()) {
      const duration = getMeetingDuration();
      // Note: pendingClip is handled by parent (VideoCall)
      onAddNote(noteText.trim(), isPrivate, duration);
      setNoteText('');
      setIsPrivate(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-[#1c1c1e] border-l border-white/10 flex flex-col z-[60] animate-slide-in">
      {/* Header */}
      <div className="bg-[#2c2c2e] border-b border-white/10 px-6 py-4">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-white text-lg font-semibold">Meeting Notes</h2>
            <p className="text-[#8e8e93] text-xs">Timestamped notes for this meeting</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
            title="Close notes"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {!isPremium && (
          <div className="mt-2 p-2 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
            <p className="text-yellow-400 text-xs">
              📝 Free tier: View-only access. Upgrade to Pro to create and edit notes.
            </p>
          </div>
        )}
      </div>

      {/* Notes Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-[#2c2c2e] rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <p className="text-white/60 text-sm">No notes yet</p>
            <p className="text-white/40 text-xs mt-1">Add your first timestamped note</p>
          </div>
        ) : (
          <>
            {notes.map((note, index) => {
              const isOwnNote = note.userName === userName;
              return (
                <div
                  key={index}
                  className="bg-[#2c2c2e] rounded-xl p-4 border border-white/10"
                >
                  {/* Note Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-[#8e8e93]">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{formatDuration(note.meetingDuration)}</span>
                        <span>•</span>
                        <span>{new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {note.isPrivate ? (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-900/30 border border-purple-700/50 rounded-full">
                          <svg className="w-3 h-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-purple-400 text-xs font-medium">Private</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-900/30 border border-green-700/50 rounded-full">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-green-400 text-xs font-medium">Shared</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Note Author */}
                  {!note.isPrivate && (
                    <div className="text-[#8e8e93] text-xs font-medium mb-2 flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-[10px] font-bold">
                        {note.userName?.[0]?.toUpperCase() || 'G'}
                      </div>
                      {note.userName || 'Guest'}
                      {isOwnNote && <span className="text-[#06c]">(You)</span>}
                    </div>
                  )}

                  {/* Note Content */}
                  <p className="text-white text-sm leading-relaxed break-words whitespace-pre-wrap">
                    {note.content}
                  </p>

                  {/* Audio context clip attachment */}
                  {note.audioClip && (
                    <ClipAttachment
                      audioClip={note.audioClip}
                      onPlay={() => onPlayClip?.(note.audioClip)}
                    />
                  )}
                </div>
              );
            })}
            <div ref={notesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      {isPremium ? (
        <form onSubmit={handleAddNote} className="bg-[#2c2c2e] border-t border-white/10 px-6 py-4">
          {/* Pending clip indicator */}
          {pendingClip && (
            <div className="mb-3 p-2 bg-blue-900/20 border border-blue-700/40 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-blue-400 text-sm font-medium">Context clip ready</p>
                  <p className="text-blue-400/60 text-xs">{pendingClip.duration}s clip will attach to your next note</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClearPendingClip}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                title="Remove clip"
              >
                <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Privacy Toggle and Clip Button Row */}
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-[#1c1c1e] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#06c] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              <span className="text-sm text-white flex items-center gap-1.5">
                {isPrivate ? (
                  <>
                    <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Private note (only you can see)
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Shared with everyone
                  </>
                )}
              </span>
            </label>

            {/* Add context clip button */}
            {!pendingClip && audioClipState && (
              <AddClipButton
                isBuffering={audioClipState.isBuffering}
                hasPermission={audioClipState.hasPermission}
                disabled={audioClipState.isRecording}
                onClick={onStartClipRecording}
              />
            )}
          </div>

          {/* Note Input */}
          <div className="flex gap-2 items-end">
            <div className="flex-1 bg-[#1c1c1e] rounded-2xl border border-white/10">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a timestamped note..."
                className="w-full bg-transparent text-white placeholder-[#8e8e93] outline-none text-sm px-4 py-3 resize-none"
                rows={3}
                maxLength={1000}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote(e);
                  }
                }}
              />
              <div className="px-4 pb-2 flex items-center justify-between">
                <span className="text-[#8e8e93] text-xs">
                  {noteText.length}/1000 • Press Enter to save
                </span>
                {meetingStartTime && (
                  <span className="text-[#8e8e93] text-xs flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formatDuration(getMeetingDuration())} in
                  </span>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={!noteText.trim()}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                noteText.trim()
                  ? 'bg-gradient-to-r from-[#06c] to-[#0055d4] hover:shadow-lg hover:scale-105'
                  : 'bg-white/10 cursor-not-allowed'
              }`}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          <p className="text-[#8e8e93] text-[10px] mt-2 text-center">
            Notes are automatically timestamped with meeting duration and time
          </p>
        </form>
      ) : (
        <div className="bg-[#2c2c2e] border-t border-white/10 px-6 py-4">
          <div className="p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700/50 rounded-xl text-center">
            <p className="text-white text-sm font-medium mb-2">Upgrade to Pro to create notes</p>
            <p className="text-[#8e8e93] text-xs mb-3">
              Create timestamped notes, share with team, and export your meeting notes
            </p>
            <button className="px-4 py-2 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all">
              Upgrade to Pro
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
